import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, catchError, throwError,of, from } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { map, mergeMap } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';
import { Router } from '@angular/router';
import { SharedServicesService } from './shared-services.service';
import { safeGetItem, safeSetItem } from '../utils/safe-storage';
@Injectable({
  providedIn: 'root',
})
export class GlobalServicesService {
  fast_api_url = environment.HOST.FAST_API_URL;
  privilegeRoles: string = '';
  pageNumber: number = 0;
  pageSize: number = 0;
  searchTerm: string = '';
  talentTerm: string = '';
  status: string = 'All';
  companyUUID: string = '';
  public searchList = new Subject<any>();
  public searchCandidateList = new Subject<any>();
  public schoolData = new Subject<any>();
  public privilegeDetails: Set<string> = new Set();

  public commonGrowl = new BehaviorSubject<{ severity?: string; summary?: string; detail?: string }>({});

  constructor(
    private http: HttpClient,
    private router: Router,
    private sharedServicesService: SharedServicesService
  ) {     
  }
  getSharedService(): SharedServicesService {
    return this.sharedServicesService;
  }
  public privilegeDetailsSubject = new BehaviorSubject<Set<string>>(new Set());
  public companyMetadataSubject = new BehaviorSubject<any>(null);
  public userMetaSubject = new BehaviorSubject<{ user_profile: any; role_details: any } | null>(null);
  public userMeta$ = this.userMetaSubject.asObservable();
  setUserMeta(user_profile: any, role_details: any) {
    this.userMetaSubject.next({ user_profile, role_details });
  }
  getCurrentUserMeta() {
    return this.userMetaSubject.getValue();
  }
  getAndStoreUserProfile(requiredPrivilege?: string | string[], onErrorRedirect: boolean = true): Observable<boolean> {
    return this.getUserProfileFastApi().pipe(
      map((res: any) => {
        const userPrivileges = res.body.auth_user_permissions || [];
        this.privilegeDetails = new Set(userPrivileges);
        this.privilegeDetailsSubject.next(this.privilegeDetails);   
        safeSetItem("userPrivilegeFastApi", JSON.stringify(userPrivileges));
        
        if (res.body.company_metadata) {
          this.sharedServicesService.setMetadata(res.body.company_metadata);
        }
        
        return true;
      }),
      catchError((error: any) => {
        const statusCode = error.status;
        if (statusCode === 502 || statusCode === 503) {
          this.sharedServicesService.setMetadataError('Service temporarily unavailable', statusCode);
          return of(false);
        }
        
        if (onErrorRedirect) {
          this.commonGrowl.next({ severity: 'warn', summary: 'warning', detail: 'Session expired. Please login again.' });
        }
        return of(false);
      })
    );
  }
  public handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof Blob) {
      return from(error.error.text()).pipe(
          mergeMap(errorText => {
              try {
                  const parsedError = JSON.parse(errorText);
                  const errorMessage = parsedError.detail || 'Unknown error occurred';
                  this.showGrowl('warn', 'Warning', errorMessage);
                  return throwError(() => new Error(errorMessage));
              } catch (e) {
                  return throwError(() => error);
              }
          })
      );
    }

    let errorMessage = 'Something went wrong; please try again later.';
    const isLoggedIn = safeGetItem('tokenFastApi') != null;
  
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client-side error: ${error.error.message}`;
      this.showGrowl('error', 'Error', errorMessage);
      return throwError(() => new Error(errorMessage));
    }
  
    if (error.status === 400 && error?.error?.detail?.includes("Invalid uuid provided")) {
      return throwError(() => new Error("No event found"));
    }
  
    this.commonGrowl.next({});
  
    switch (error.status) {
      case 400:
        if (!this.commonGrowl.getValue().detail) {
          this.showGrowl('warn', 'Warning', error?.error?.error?.message || error?.error?.detail || 'Bad Request');
        }
        errorMessage = error?.error?.error?.message || error?.error?.detail || 'Bad Request';
        break;
  
      case 401:
        if (isLoggedIn) {
          this.showGrowl('error', 'Error', 'Unauthorized');
          errorMessage = 'Unauthorized';
        } else {
          this.router.navigate(['/logout']);
        }
        break;
  
        case 403:
          const isSessionExpired = 
            error.error.detail === 'Session has expired' || 
            JSON.stringify(error.error) === '{"detail":"Session has expired"}';
          if (isSessionExpired) {
            this.showGrowl('warn', 'Warning', 'Session has expired');
            this.router.navigate(['/logout']);
          } else {
            this.showGrowl('error', 'Error', error.error.detail || 'Access denied');
          }
          errorMessage = error.error.detail || 'Access denied';
          break;
  
      case 404:
        this.showGrowl('warn', 'Failed', error.error.detail || 'Not found');
        errorMessage = 'Not found';
        break;
  
      case 422:
        const detail = Array.isArray(error.error.detail) ? 
          error.error.detail.map((err: any) => err.msg).join(', ') : 
          error.error.detail;
        this.showGrowl('warn', 'Warning', detail);
        errorMessage = detail;
        break;
  
      case 500:
        this.showGrowl('error', 'Error', 'Internal Server Error');
        errorMessage = 'Internal Server Error';
        break;
  
      default:
        this.showGrowl('error', 'Error', `Error: ${error.message}`);
        errorMessage = `Error: ${error.message}`;
    }
  
    return throwError(() => new Error(errorMessage));
  }
  
  private showGrowl(severity: string, summary: string, detail: string): void {
    this.commonGrowl.next({ severity, summary, detail });
  }

  options_fast_api_for_domain() {
    const domainFromUrl = new URL(window.location.href).hostname;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      domain: domainFromUrl || '',
    });
    return { headers };
  }

  metadataFastApi(): Observable<HttpResponse<any>> {
    return this.http.get<HttpResponse<any>>(this.fast_api_url + '/metadata/company', {
      observe: 'response',
      ...this.options_fast_api_for_domain()
    }).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  //fast apis
  getUserProfileFastApi(): Observable<HttpResponse<any>> { 
    return this.http.get<HttpResponse<any>>(this.fast_api_url + '/users/profile', {
      observe: 'response'
    }).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => error))
    ); 
  }

  getJobRolesLocationFastApi(): Observable<HttpResponse<any>> {
    return this.http.get<HttpResponse<any>>(`${this.fast_api_url}/job-roles/location`, {
      observe: 'response'
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  getCurentPageUrl() {
    return window.location.pathname;
  }

  loadScript(
    element: string,
    url: string,
    type: string,
    id: string,
    child?: any[],
    defer?: any,
    onload?: any,
    onerror?: any
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (document.getElementById(id)) {
          resolve(true);
        return;
      }
  
      const node: any = document.createElement(element);
      if (element === 'link') {
        node.href = url;
        node.rel = 'stylesheet';
      } else if (element === 'script') {
        node.src = url;
      }
  
      if (defer) {
        node.defer = defer;
      }
  
      node.type = type;
      node.id = id;
  
      node.onload = () => {
        if (child && child.length) {
          const promiseArray: any[] = [];
          child.forEach((script: any) => {
            promiseArray.push(
              this.loadScript(
                script.element,
                script.url,
                script.type,
                script.id,
                script.child
              )
            );
          });
          Promise.all(promiseArray).then(() => {
            resolve(true);
          });
        } else {
          setTimeout(() => {
            resolve(true);
          }, 100);
        }
      };
  
      node.onerror = (error: any) => {
        reject(error);
      };
  
      document.getElementsByTagName('head')[0].appendChild(node);
    });
  }

  removeScript() {
    let script = document.getElementById("examlyck")
    if (script) {
      script.remove();
    }
  }

  ssoLoginFastApi(payload: any): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(this.fast_api_url + '/manage-contest/hire/sso_login',payload, {
      observe: 'response'
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }


}